/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '.';
import { useWakeLock } from 'react-screen-wake-lock';
import { doStream, endStream } from '../../utils/listen';
import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import {
  createEncounter,
  completeEncounter,
  updateEncounter,
  startEncounter,
} from '../../store/actions/encounter';
import { MicrophoneLevel } from './MicrophoneLevel';
import AmbientAnimation from '../CurrentEncounter/AmbientAnimation';
import AmbientAnimationPaused from '../CurrentEncounter/AmbientAnimationPaused';
import { AmbientpauseIcon, ResumeIcon } from 'src/icons';
import TemplateSelectDropdown from '../Settings/Settings.TemplateDropdown';
import { Modal } from 'antd';
import { Bars } from 'react-loader-spinner';
import { PatientNotes } from '../../../appointments/components/patientNotes/PatientNotes';
import GenerateSummaryButton from './GenerateSummaryButton';
import { analytics } from 'src/services';

const PreChartButton = ({
  selectedMicrophone,
  topBarInputs,
  setActiveTab,
  storedParams,
  selectedRecord,
  encounterStatus,
  encounterPhase,
  setEncounterStatus,
  setLiveTranscript,
  setRecord,
  setLivePartialTranscript,
  setActiveRightPanelTab,
  isStreaming,
  setIsStreaming,
  isRecording,
  setIsRecording,
  schedulepage,
  restrictTemplates,
  patientDetails,
  settingsOrgValue,
}) => {
  const [micLevel, setMicLevel] = useState(0);
  const [showSaveDeleteButtons, setShowSaveDeleteButtons] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const { isEntitled: isPatientHistoryEntitled } =
    useFeatureEntitlements('patient-history');

  const { isEntitled: hasICD10Access } = useFeatureEntitlements('icd-10');

  const { isEntitled: canStartPreChart } = usePermissionEntitlements(
    'encounter.pre-chart.start'
  );
  const { isEntitled: canGeneratePreChartSummary } = usePermissionEntitlements(
    'encounter.pre-chart.generate'
  );

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { encounterDetails, mobileViewStatus } = useSelector(
    (state) => state?.encounterState
  );
  const { templateListLoading, templateOverridesListLoading } = useSelector(
    (state) => state?.summaryState
  );

  const { user } = useAuth();
  const dispatch = useDispatch();
  const { isSupported, released, request, release } = useWakeLock();
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [templateOverridesId, setTemplateOverridesId] = useState(null);
  const record =
    selectedRecord !== undefined ? selectedRecord : mobileViewStatus;
  const isMobile = window.innerWidth <= 1260;
  useEffect(() => {
    if (!isSupported) {
      console.warn('Screen Wake Lock is not supported');
      return;
    }
    if (isRecording) {
      request().catch((err) => {
        console.error(err);
      });
    } else {
      if (!released) {
        release();
      }
    }
  }, [isRecording, request, release, isSupported, released]);

  useEffect(() => {
    setCurrentTemplate({});
  }, [encounterPhase]);

  useEffect(() => {
    if (encounterDetails) {
      if (encounterPhase == 'in-visit') {
        setTemplateId(encounterDetails.in_visit_template_id);
        setTemplateOverridesId(encounterDetails.in_visit_template_overrides_id);
      } else if (encounterPhase == 'pre-chart') {
        setTemplateId(encounterDetails.pre_chart_template_id);
        setTemplateOverridesId(
          encounterDetails.pre_chart_template_overrides_id
        );
      }
    }
  }, [encounterDetails, encounterPhase]);

  const handleCreate = () => {
    const data = {
      practitioner_id: currentPractitioner.practitioner_id,
      start_time: new Date(),
      type: 'ambient',
      description: topBarInputs?.description,
      template_id: currentTemplate?.template_id,
      template_overrides_id: currentTemplate?.template_overrides_id,
    };
    dispatch(createEncounter(data));
  };

  const handleSocketErrors = (error) => {
    // eslint-disable-next-line no-undef
    analytics.track(`User Run Into Socket Error: ${error}, Pausing Encounter`, {
      email: user?.email,
    });
    let modalOptions = {
      centered: true,
      className: 'font-sans',
    };
    if (error === 'MICROPHONE_SETUP_FAILED') {
      Modal.error({
        ...modalOptions,
        title: 'Microphone setup failed',
        content:
          'Unable to access a microphone. Please check your microphone settings and permissions.',
      });
    } else if (error === 'AUDIO_CONTEXT_SUSPENDED') {
      Modal.warning({
        ...modalOptions,
        title: 'Microphone was suspended',
        content:
          'Microphone was suspended. Please click resume to continue transcribing.',
      });
    } else if (error === 'NETWORK_ERROR') {
      Modal.error({
        ...modalOptions,
        title: 'Network error',
        content:
          'You were offline for more than 1 minute. Please click resume to continue transcribing.',
      });
    } else if (error === 'NETWORK_INIT_ERROR') {
      Modal.error({
        ...modalOptions,
        title: 'Network offline',
        content:
          'Could not connect to server. Please check your connection and try again.',
      });
    } else if (error === 'MAX_RECONNECTIONS_EXCEEDED') {
      Modal.error({
        ...modalOptions,
        title: 'Internal server error',
        content:
          'Maximum reconnection attempts reached. Please try again or contact support.',
      });
    }
    handleCleanupRecording();

    const audio = new Audio('src/assets/error.mp3');
    audio.play();
  };

  const [isTranscriptTimeout, setIsTranscriptTimeout] = useState(false);

  const handleTranscriptTimeout = (error) => {
    if (error === 'NO_TRANSCRIPTION_RECEIVED') {
      setIsTranscriptTimeout(true);
    } else if (error !== 'NO_TRANSCRIPTION_RECEIVED') {
      setIsTranscriptTimeout(false);
    }
  };

  const handleCleanupRecording = () => {
    setIsRecording(false);
    setIsStreaming(false);
    endStream();
  };

  //Function when pause recording is clicked
  const handlePauseRecording = () => {
    setIsRecording(false);
    setIsStreaming(false);
    endStream();
    const noteType =
      encounterPhase === 'in-visit'
        ? 'Visit'
        : encounterPhase === 'pre-chart'
          ? 'Pre-charting'
          : '';
    if (schedulepage) {
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked Pause ${noteType} In Ambient From VCA`, {
        email: user?.email,
      });
    } else {
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked Pause ${noteType} In Ambient From Aura`, {
        email: user?.email,
      });
    }
  };

  //Function when resume recording is clicked
  const handleResumeRecording = () => {
    let data = {
      template_id: currentTemplate?.template_id,
      template_overrides_id: currentTemplate?.template_overrides_id,
    };
    dispatch(
      updateEncounter(
        record?.encounter_id,
        encounterPhase,
        data,
        storedParams,
        false
      )
    );
    doStream({
      encounterId: record?.encounter_id,
      encounterPhase,
      setMicLevel,
      setLiveTranscript,
      setLivePartialTranscript,
      selectedMicrophoneId: selectedMicrophone,
      successCallback: () => {
        setIsStreaming(true);
      },
      failureCallback: (error) => {
        handleSocketErrors(error);
      },
      setDictation: () => {},
      isDictate: false,
      transcriptTimeoutCallback: (error) => {
        handleTranscriptTimeout(error);
      },
    });
    setIsRecording(true);
    const noteType =
      encounterPhase === 'in-visit'
        ? 'Visit'
        : encounterPhase === 'pre-chart'
          ? 'Pre-charting'
          : '';
    if (schedulepage) {
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked Resume ${noteType} In Ambient From VCA`, {
        email: user?.email,
      });
    } else {
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked Resume ${noteType} In Ambient From Aura`, {
        email: user?.email,
      });
    }
  };

  //Function when stop recording is clicked
  const handleStopRecording = () => {
    setShowSaveDeleteButtons(true);
    handlePauseRecording();
    setIsStreaming(false);
    setMicLevel(0);
    if (schedulepage) {
      // eslint-disable-next-line no-undef
      analytics.track('Clicked Stop Recording In Ambient From VCA', {
        email: user?.email,
      });
    } else {
      // eslint-disable-next-line no-undef
      analytics.track('Clicked Stop Recording In Ambient From Aura', {
        email: user?.email,
      });
    }
  };

  //Function when complete recording is clicked
  const handleCompleteRecording = () => {
    setEncounterStatus('summary_inprogress');

    setShowSaveDeleteButtons(false);
    dispatch(
      completeEncounter(record?.encounter_id, encounterPhase, storedParams)
    );
    setIsStreaming(false);
    setIsRecording(false);
    setMicLevel(0);
    endStream();
    setActiveTab ? setActiveTab('note') : null;
    const noteType =
      encounterPhase === 'in-visit'
        ? 'Note'
        : encounterPhase === 'after-visit'
          ? 'AVS'
          : 'Pre-chart';

    if (hasICD10Access && encounterPhase == 'in-visit') {
      setActiveRightPanelTab && setActiveRightPanelTab('icd10');
    }

    if (schedulepage) {
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked Generate ${noteType} In Ambient From VCA`, {
        email: user?.email,
      });
    } else {
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked Generate ${noteType} In Ambient From Aura`, {
        email: user?.email,
      });
    }
  };

  const handleStartEncounter = (encounter_phase) => {
    setIsRecording(true);
    setIsStarting(true);
    dispatch(
      startEncounter(
        record?.encounter_id,
        encounter_phase,
        {
          template_id: currentTemplate?.template_id,
          template_overrides_id: currentTemplate?.template_overrides_id,
        },
        storedParams,
        (newRecord) => {
          setRecord(newRecord);
        }
      )
    );

    doStream({
      encounterId: record?.encounter_id,
      encounterPhase,
      setMicLevel,
      setLiveTranscript,
      setLivePartialTranscript,
      selectedMicrophoneId: selectedMicrophone,
      successCallback: () => {
        setIsStreaming(true);
        setIsStarting(false);
      },
      failureCallback: (error) => {
        handleSocketErrors(error);
      },
      setDictation: () => {},
      isDictate: false,
      transcriptTimeoutCallback: (error) => {
        handleTranscriptTimeout(error);
      },
    });
    const noteType =
      encounterPhase === 'in-visit'
        ? 'Visit'
        : encounterPhase === 'pre-chart'
          ? 'Pre-charting'
          : '';
    if (schedulepage) {
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked Start ${noteType} In Ambient From VCA`, {
        email: user?.email,
      });
    } else {
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked Start ${noteType} In Ambient From Aura`, {
        email: user?.email,
      });
    }
  };

  const handleStartOrResumeRecording = () => {
    if (record?.encounter_id) {
      handleResumeRecording();
    } else {
      handleCreate();
      setIsRecording(true);
      if (schedulepage) {
        // eslint-disable-next-line no-undef
        analytics.track('Clicked New Recording In Ambient From VCA', {
          email: user?.email,
        });
      } else {
        // eslint-disable-next-line no-undef
        analytics.track('Clicked New Recording In Ambient From Aura', {
          email: user?.email,
        });
      }
    }
  };

  const ifShowRecordingControls = () => {
    return (
      encounterStatus === 'new' ||
      encounterStatus === 'inprogress' ||
      !encounterDetails?.encounter_id
    );
  };

  const handlePauseResumeToggle = () => {
    if (isRecording) {
      handlePauseRecording();
    } else {
      handleResumeRecording();
    }
  };

  const onAuraIconClick = () => {
    if (!canStartPreChart) {
      return;
    }
    if (encounterStatus === 'new') {
      handleStartEncounter(encounterPhase);
    } else {
      handleResumeRecording();
    }
  };

  return encounterStatus &&
    !templateListLoading &&
    !templateOverridesListLoading ? (
    <>
      <div
        style={{ maxWidth: 'min(400px, 100%)', minWidth: 'min(320px, 100%)' }}
        className="rounded-xl flex flex-col items-center justify-center relative current p-3"
      >
        <div
          className={`${
            isMobile ? 'w-full' : ''
          } flex items-center justify-center`}
        >
          {encounterPhase != 'after-visit' && (
            <div className="">
              {isStreaming && !showSaveDeleteButtons ? (
                // will trigger once the recording is started/resumed
                <div className="cursor-pointer" onClick={handlePauseRecording}>
                  <AmbientAnimation />
                  <div className="w-full mb-4">
                    <MicrophoneLevel
                      micLevel={micLevel}
                      isTranscriptTimeout={isTranscriptTimeout}
                    />
                  </div>
                </div>
              ) : (
                // will trigger once the recording is paused
                <div className="cursor-pointer" onClick={onAuraIconClick}>
                  <AmbientAnimationPaused />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="w-full flex justify-center items-center">
          {encounterPhase != 'after-visit' &&
            ifShowRecordingControls() &&
            !showSaveDeleteButtons && (
              <div className="w-full mt-2">
                <div
                  className={[
                    'w-full',
                    isRecording && 'pointer-events-none opacity-50',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="text-[#667085] mb-1">Note template</div>
                  <TemplateSelectDropdown
                    allowInlineEdit={true}
                    encounterPhase={encounterPhase}
                    currentTemplate={currentTemplate}
                    setCurrentTemplate={setCurrentTemplate}
                    showToast={false}
                    templateId={templateId}
                    templateOverridesId={templateOverridesId}
                    fromAuraPage={!schedulepage}
                    restrictTemplates={restrictTemplates}
                  />
                </div>

                {isStreaming ? (
                  // will trigger once the recording is started/resumed
                  <>
                    <div className="flex space-x-4 mt-5 justify-center">
                      <Button
                        id={'pause_record'}
                        type={'button'}
                        className="w-full bg-[#A9A9A9] hover:bg-[#696969] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
                        onClick={handlePauseResumeToggle}
                      >
                        <>
                          <span className="mr-2 mt-0">
                            <AmbientpauseIcon />
                          </span>
                          Pause pre-charting
                        </>
                      </Button>
                    </div>
                    <div className="flex space-x-4 mt-4 justify-center">
                      <GenerateSummaryButton
                        encounter={encounterDetails}
                        encounterPhase={encounterPhase}
                        handleStopRecording={handleStopRecording}
                        handleCompleteRecording={handleCompleteRecording}
                      />
                    </div>
                  </>
                ) : encounterStatus !== 'new' && !isStarting ? (
                  // will trigger once the recording is paused
                  <>
                    <div className="flex mt-5 justify-center">
                      <div className="bg-[#A9A9A9] hover:bg-[#696969] divide-x divide-white w-full rounded-xl flex justify-center items-center">
                        <Button
                          type={'button'}
                          className="w-full bg-[#A9A9A9] hover:bg-[#696969] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
                          onClick={handlePauseResumeToggle}
                          disabled={!canStartPreChart}
                        >
                          {isRecording ? (
                            <>
                              <span className="mr-2 mt-0">
                                <ResumeIcon />
                              </span>
                              Resuming ...
                            </>
                          ) : (
                            <>
                              <span className="mr-2 mt-0">
                                <ResumeIcon />
                              </span>
                              Resume pre-charting
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="flex space-x-4 mt-4 justify-center">
                      <GenerateSummaryButton
                        encounter={encounterDetails}
                        handleStopRecording={handleStopRecording}
                        handleCompleteRecording={handleCompleteRecording}
                        encounterPhase={encounterPhase}
                      />
                    </div>
                    {encounterPhase == 'pre-chart' && (
                      <div className="mt-4">
                        <PatientNotes
                          schedulepage={schedulepage}
                          isEnabled={isPatientHistoryEntitled}
                          source="pre_chart_history"
                          patientDetails={patientDetails}
                          encounter={record}
                          currentPractitioner={currentPractitioner}
                          settingsOrgValue={settingsOrgValue}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-full rounded-xl flex justify-center items-center mt-5">
                      <Button
                        id={'start_record'}
                        type={'button'}
                        className={'w-full'}
                        onClick={() => handleStartEncounter(encounterPhase)}
                      >
                        {isRecording ? (
                          <>
                            <span className="mr-2 mt-0">
                              <ResumeIcon />
                            </span>
                            Starting ...
                          </>
                        ) : (
                          <>
                            <span className="mr-2 mt-0">
                              <ResumeIcon />
                            </span>
                            Start pre-charting
                          </>
                        )}
                      </Button>
                    </div>
                    {encounterPhase == 'pre-chart' && (
                      <div className="mt-4">
                        <PatientNotes
                          schedulepage={schedulepage}
                          isEnabled={isPatientHistoryEntitled}
                          source="pre_chart_history"
                          patientDetails={patientDetails}
                          encounter={record}
                          currentPractitioner={currentPractitioner}
                          settingsOrgValue={settingsOrgValue}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
        </div>
      </div>
    </>
  ) : (
    <>
      <Bars
        height="25"
        width="25"
        color="#00D090"
        ariaLabel="bars-loading"
        wrapperStyle={{}}
        wrapperClass="justify-center"
        visible={true}
      />
    </>
  );
};

export default PreChartButton;
